import React from "react";

/**
 * Animated paper-cut wave hero — replaces the static Unsplash image on the
 * Landing page. Five SVG wave layers drift at different speeds and directions
 * for a parallax-video feel. Pure CSS keyframe animation, no video files.
 *
 * Each wave path is rendered twice side-by-side (double-width) and the layer
 * translates from 0 → -50% on a linear loop, so the wave appears to scroll
 * forever without seams.
 *
 * Respects prefers-reduced-motion (pauses all motion).
 */
export default function AnimatedWaveHero({
  liveLabel = "LIVE",
  overline = "NOW STREAMING",
  title = "Launch a stream. Get discovered.",
  subtitle = "Become a verified seller",
  className = "",
}) {
  return (
    <div
      className={`relative w-full h-full overflow-hidden bg-[#0a1530] ${className}`}
      data-testid="animated-wave-hero"
    >
      {/* Background gradient — deep navy at top fading to slightly lifted teal-navy */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 80% at 50% -10%, #0e1b3c 0%, #0a1530 45%, #060c20 100%)",
        }}
      />
      {/* Soft glow blob to fake light source — animated breath */}
      <div
        aria-hidden="true"
        className="absolute -top-1/3 right-0 w-[120%] h-[80%] wave-glow"
        style={{
          background:
            "radial-gradient(closest-side, rgba(33,144,204,0.22), transparent 70%)",
          filter: "blur(40px)",
        }}
      />

      {/* Wave layers — order matters; back-to-front. y/height tuned to match
          the dramatic paper-cut crest of the reference design. */}
      <div className="absolute inset-0 wave-bob" aria-hidden="true">
        {/* Layer 1 (back, deepest blue, slow right→left drift) */}
        <WaveLayer
          className="s1"
          color="#13315c"
          opacity={0.85}
          y="32%"
          height="78%"
          phase={0}
        />
        {/* Layer 2 (mid-back, drifts opposite, brighter) */}
        <WaveLayer
          className="s2"
          color="#1a4475"
          opacity={0.92}
          y="42%"
          height="72%"
          phase={120}
        />
        {/* Layer 3 (mid-front, slightly amber-tinted highlights) */}
        <WaveLayer
          className="s3"
          color="#205791"
          opacity={0.95}
          y="52%"
          height="64%"
          phase={70}
          glint
        />
        {/* Layer 4 (front, lightest blue, fastest) */}
        <WaveLayer
          className="s4"
          color="#2b6ca8"
          opacity={1}
          y="62%"
          height="56%"
          phase={200}
        />
      </div>

      {/* Top-down darken so text bottom is legible */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.05) 50%, rgba(0,0,0,0) 100%)",
        }}
      />

      {/* LIVE pill — top-left */}
      <div className="absolute top-4 left-4 pill pill-live z-10" data-testid="hero-live-pill">
        <span className="h-1.5 w-1.5 rounded-full bg-white live-dot" /> {liveLabel}
      </div>

      {/* Headline block — bottom-left */}
      <div className="absolute bottom-5 left-5 right-5 z-10">
        <div className="overline text-white/80">{overline}</div>
        <div className="font-display font-bold text-2xl leading-tight mt-1 text-white">
          {title}
        </div>
        <div className="text-xs text-white/70 mt-1">{subtitle}</div>
      </div>
    </div>
  );
}

/**
 * Single wave layer. Renders ONE SVG that's 2× the viewport wide and contains
 * two copies of the wave path side-by-side. The container `<div>` then
 * translates -50% across its keyframe → seamless loop.
 *
 * `phase` (0..359) shifts the y-control points so each layer has a unique
 * silhouette and they don't overlap into a single boring band.
 */
function WaveLayer({ className, color, opacity = 1, y = "60%", height = "50%", phase = 0, glint = false }) {
  // Build a sine-ish wave path. Width 200, x repeats so SVG can tile.
  // We bake the path as 4 humps over 200 units; the layer renders 2 copies
  // (totalling 400 units = 200% width) so translate-50% looks seamless.
  const path = buildWavePath(phase);

  return (
    <div
      className={`wave-layer absolute left-0 ${className}`}
      style={{
        top: y,
        height,
        width: "200%",
        opacity,
      }}
    >
      <svg
        viewBox="0 0 400 100"
        preserveAspectRatio="none"
        width="100%"
        height="100%"
        aria-hidden="true"
      >
        <defs>
          {glint && (
            <linearGradient id={`glint-${phase}`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={color} />
              <stop offset="60%" stopColor={color} />
              <stop offset="78%" stopColor="#3a8fc6" />
              <stop offset="86%" stopColor="#a37a3a" stopOpacity="0.7" />
              <stop offset="100%" stopColor={color} />
            </linearGradient>
          )}
        </defs>
        <path
          d={`${path} L 400 100 L 0 100 Z`}
          fill={glint ? `url(#glint-${phase})` : color}
        />
      </svg>
    </div>
  );
}

/** Build a 4-hump sine wave path 0..200 wide, then duplicate to 400 wide.
 *  `phase` shifts the wave horizontally so layers feel different. */
function buildWavePath(phase = 0) {
  const ph = (phase / 360) * 200; // 0..200
  const segments = 8; // 8 control segments across 200
  const stepX = 200 / segments;
  const points = [];
  for (let i = 0; i <= segments; i++) {
    const x = i * stepX;
    const t = (x + ph) / 200;
    // y in 0..100 SVG units; sine, range 25..70
    const y = 45 + 22 * Math.sin(t * Math.PI * 2) + 8 * Math.sin(t * Math.PI * 4 + 1);
    points.push([x, y]);
  }
  // Smooth path with cubic curves between adjacent points (Catmull-Rom -> Bezier).
  let d = `M ${points[0][0]} ${points[0][1]}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
    const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
    const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
    const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${p2[0].toFixed(2)} ${p2[1].toFixed(2)}`;
  }
  // Duplicate the wave to fill 200..400 (seamless tile)
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    const cp1x = p1[0] + 200 + (p2[0] - p0[0]) / 6;
    const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
    const cp2x = p2[0] + 200 - (p3[0] - p1[0]) / 6;
    const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${(p2[0] + 200).toFixed(2)} ${p2[1].toFixed(2)}`;
  }
  return d;
}
